import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRestaurantBySlug } from '../lib/hooks';
import { supabase } from '../lib/supabase';
import { Table } from '../lib/types';
import { useTableOrder } from '../lib/table-context';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Utensils, QrCode, AlertTriangle } from 'lucide-react';
export function TablePage() {
  const {
    slug,
    tableNumber,
    token
  } = useParams<{
    slug: string;
    tableNumber: string;
    token: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    setTableOrder
  } = useTableOrder();
  const [table, setTable] = useState<Table | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'invalid_qr' | 'inactive' | null>(null);
  useEffect(() => {
    // If restaurant lookup is done but no restaurant found, stop loading
    if (!restLoading && !restaurant) {
      setLoading(false);
      setError('not_found');
      return;
    }
    if (!restaurant || !tableNumber) return;
    async function fetchTable() {
      try {
        setLoading(true);
        setError(null);
        const {
          data,
          error: fetchError
        } = await supabase.from('tables').select('*').eq('restaurant_id', restaurant!.id).eq('table_number', tableNumber!).single();
        if (fetchError || !data) {
          setError('not_found');
          return;
        }
        // Check if table is active
        if (!data.is_active) {
          setError('inactive');
          return;
        }
        // If token is provided, validate it
        if (token && data.qr_token && data.qr_token !== token) {
          setError('invalid_qr');
          return;
        }
        setTable(data);
        // Set table order context for QR flow
        setTableOrder({
          tableId: data.id,
          tableNumber: data.table_number,
          tableName: data.table_name || `Mesa ${data.table_number}`,
          restaurantSlug: restaurant!.slug,
          restaurantId: restaurant!.id,
          orderChannel: token ? 'qr' : 'qr' // Both paths are self-service
        });
        // Also keep legacy localStorage for backward compat
        localStorage.setItem('noiza_current_table', data.id);
      } catch (err) {
        console.error('[NOIZA][TablePage] Error fetching table:', err, {
          restaurant_id: restaurant?.id,
          tableNumber,
          token
        });
        setError('not_found');
      } finally {
        setLoading(false);
      }
    }
    fetchTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant, tableNumber, token]);
  if (restLoading || loading) {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-8161" data-mp="1jmzq7sup9bhj">
        <Skeleton className="w-full max-w-md h-64 rounded-2xl" data-id="element-8162" data-mp="1b562g5wde6l4" />
      </div>;
  }
  // Error states
  if (error === 'invalid_qr') {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-8163" data-mp="173ue03p8frei">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden text-center p-8" data-id="element-8164" data-mp="1l33smyxsidgd">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8165" data-mp="1g5ljp8jrvy8d">
            <AlertTriangle className="w-8 h-8 text-red-500" data-id="element-8166" data-mp="sd502tkdzy6y" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-8167" data-mp="nhpok89uqedp">QR Inválido</h1>
          <p className="text-gray-600 mb-6" data-id="element-8168" data-mp="1262iz0tajp63">
            Este código QR no es válido o ha expirado. Por favor, consulta con
            el personal del restaurante.
          </p>
          <Button variant="secondary" onClick={() => navigate('/')} data-id="element-8169" data-mp="9o6mudddz9mb">
            Volver al inicio
          </Button>
        </div>
      </div>;
  }
  if (error === 'inactive') {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-8170" data-mp="34460zwobgwnx">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden text-center p-8" data-id="element-8171" data-mp="392h9a2yyqz41">
          <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8172" data-mp="3d3srvojf6k2q">
            <AlertTriangle className="w-8 h-8 text-amber-500" data-id="element-8173" data-mp="3hvas2admcgmn" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-8174" data-mp="3mudxvx1faqvo">
            Mesa No Disponible
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-8175" data-mp="3rsp6y5jw8xcu">
            Esta mesa no está disponible en este momento. Por favor, consulta
            con el personal.
          </p>
          <Button variant="secondary" onClick={() => navigate('/')} data-id="element-8176" data-mp="l2ze1u20aqp">
            Volver al inicio
          </Button>
        </div>
      </div>;
  }
  if (!restaurant || !table || error === 'not_found') {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center p-4" data-id="element-8177" data-mp="26fm4phwgdflb">
        <EmptyState icon={QrCode} title="Mesa no encontrada" description="Este código QR no parece ser válido o la mesa no existe." actionLabel="Ir al inicio" onAction={() => navigate('/')} data-id="element-8178" data-mp="1ywcvppruziws" />
      </div>;
  }
  const primaryColor = restaurant.primary_color || '#F59E0B';
  return <div className="min-h-screen bg-brand-offwhite flex flex-col items-center justify-center p-4" data-id="element-8179" data-mp="1l5zr9xe6emc7">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden text-center" data-id="element-8180" data-mp="2htxlcz2g6lig">
        <div className="h-32 w-full flex items-center justify-center" style={{
        backgroundColor: primaryColor
      }} data-id="element-8181" data-mp="2cxfjq82ez3ac">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-20 h-20 rounded-2xl shadow-lg object-cover bg-white" data-id="element-8182" data-mp="23uo6c2pz9xg2" /> : <div className="w-20 h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center text-3xl font-bold" style={{
          color: primaryColor
        }} data-id="element-8183" data-mp="sfhjij2ibwsg">
              {restaurant.name.charAt(0)}
            </div>}
        </div>

        <div className="p-8" data-id="element-8184" data-mp="kwi5ksxtexgh">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-8185" data-mp="oyd2zmcmkqoz">
            Bienvenido a {restaurant.name}
          </h1>

          <div className="inline-flex items-center justify-center gap-2 bg-emerald-50 px-4 py-2 rounded-full mb-2" data-id="element-8186" data-mp="b6gjg6p0vp47">
            <Utensils className="w-4 h-4 text-emerald-600" data-id="element-8187" data-mp="fyqbbs9p6z94" />
            <span className="font-semibold text-emerald-700" data-id="element-8188" data-mp="18showt01hcj">
              {table.table_name || `Mesa ${table.table_number}`}
            </span>
          </div>

          <div className="inline-flex items-center justify-center gap-1.5 bg-emerald-50/50 px-3 py-1 rounded-full mb-6" data-id="element-8189" data-mp="6856vo7cf0ia">
            <QrCode className="w-3.5 h-3.5 text-emerald-500" data-id="element-8190" data-mp="3ofun1ijesa5o" />
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-8191" data-mp="3tba0wahf2623">
              Autoatención
            </span>
          </div>

          <p className="text-gray-600 mb-8" data-id="element-8192" data-mp="34ryezff6wtrk">
            Estás listo para ordenar desde tu mesa. Explora nuestro menú y tu
            pedido llegará directamente aquí.
          </p>

          <Button size="lg" fullWidth onClick={() => navigate(slug ? `/r/${slug}/menu` : '/menu')} style={{
          backgroundColor: primaryColor
        }} className="text-white shadow-lg" data-id="element-8193" data-mp="3ip4zbefkb0iw">
            Ver Menú y Ordenar
          </Button>
        </div>
      </div>
    </div>;
}